import React, { useState, useEffect } from 'react';
import {
  useQueryParams,
  StringParam,
  withDefault,
  NumberParam,
} from 'use-query-params';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../commons/Auth';
import { useAlert } from 'react-alert';
import { useLoading } from '../../commons/Loading';
import ContractsPresentational from './ContractsPresentational';
import contractsAPIs from '../../lib/fetch/contractsAPIs';
import { getSignedUrl } from '../../lib/fetch/files';
const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const ContractsSuperAdmin = ({ profile }) => {
  const [contracts, setContracts] = useState([]);
  const [formattedCSVContracts, setFormattedCSVContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [{ newToken } = {}] = useAuth();
  const [hasImportedContracts, setHasImportedContracts] = useState(false);
  const [
    contractsImportFeedbackData,
    setContractsImportFeedbackData,
  ] = useState({});
  const [t] = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();
  const [totPod, setTotPod] = useState(0);
  const [totPdr, setTotPdr] = useState(0);

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    year: NumberParam,
    pod_pdr: withDefault(StringParam, ''),
    owner: withDefault(StringParam, ''),
    holder: withDefault(StringParam, ''),
    condo_vat_cf: withDefault(StringParam, ''),
    address: withDefault(StringParam, ''),
    customer: withDefault(StringParam, ''),
    customerType: withDefault(StringParam, ''),
    managementStatus: withDefault(StringParam, ''),
    contractStatus: withDefault(StringParam, ''),
    contractSigningYear: NumberParam,
    contractSigningMonth: NumberParam,
    contractEndYear: NumberParam,
    contractEndMonth: NumberParam,
    contractType: withDefault(StringParam, ''),
    supplier: withDefault(StringParam, ''),
  });

  const {
    year,
    limit,
    offset,
    condo_vat_cf,
    owner,
    holder,
    pod_pdr,
    customer,
    customerType,
    managementStatus,
    contractStatus,
    contractSigningYear,
    contractSigningMonth,
    contractEndYear,
    contractEndMonth,
    contractType,
    supplier,
  } = query;

  const toggleShowImportFeedback = () => setHasImportedContracts((v) => !v);

  function onFilter(queryDiff) {
    if (
      (queryDiff.pod_pdr && queryDiff.pod_pdr !== pod_pdr) || // first search
      (pod_pdr && queryDiff.pod_pdr === '') ||
      (queryDiff.contractSigningYear &&
        queryDiff.contractSigningYear !== contractSigningYear) || // first search
      (contractSigningYear && queryDiff.contractSigningYear === '') || // search deleted
      (queryDiff.contractSigningMonth &&
        queryDiff.contractSigningMonth !== contractSigningMonth) || // first search
      (contractSigningMonth && queryDiff.contractSigningMonth === '') || // search deleted
      (queryDiff.contractEndYear &&
        queryDiff.contractEndYear !== contractEndYear) || // first search
      (contractEndYear && queryDiff.contractEndYear === '') || // search deleted
      (queryDiff.contractEndMonth &&
        queryDiff.contractEndMonth !== contractEndMonth) || // first search
      (contractEndMonth && queryDiff.contractEndMonth === '') || // search deleted
      (queryDiff.customer && queryDiff.customer !== customer) || // first search
      (customer && queryDiff.customer === '') || // search deleted
      (queryDiff.customerType && queryDiff.customerType !== customerType) || // first search
      (customerType && queryDiff.customerType === '') || // search deleted
      (queryDiff.contractType && queryDiff.contractType !== contractType) || // first search
      (contractType && queryDiff.contractType === '') || // search deleted
      (queryDiff.supplier && queryDiff.supplier !== supplier) || // first search
      (supplier && queryDiff.supplier === '') || // search deleted
      (queryDiff.managementStatus &&
        queryDiff.managementStatus !== managementStatus) || // first search
      (managementStatus && queryDiff.managementStatus === '') || // search deleted
      (queryDiff.contractStatus &&
        queryDiff.contractStatus !== contractStatus) || // first search
      (contractStatus && queryDiff.contractStatus === '') || // search deleted
      (queryDiff.condo_vat_cf && queryDiff.condo_vat_cf !== condo_vat_cf) || // first search
      (condo_vat_cf && queryDiff.condo_vat_cf === '') || // search deleted
      (queryDiff.owner && queryDiff.owner !== owner) || // first search
      (owner && queryDiff.owner === '') ||
      (queryDiff.holder && queryDiff.holder !== holder) || // first search
      (holder && queryDiff.holder === '') ||
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  const fetchContracts = async (query) => {
    showLoading();
    const {
      data,
      total: _total,
      totPod,
      totPdr,
    } = await contractsAPIs.findAllForAdmin({
      newToken,
      query,
    });
    hideLoading();

    setTotal(Number(_total || 0));
    setTotPod(totPod);
    setTotPdr(totPdr);

    setContracts(data);
  };

  const fetchContractsForExport = async (query) => {
    const {
      data,
      total: _total,
      formattedCSVContracts,
    } = await contractsAPIs.findForExportAdminAll({ newToken, query });
    setFormattedCSVContracts(formattedCSVContracts);
  };

  const onImportContract = async (file) => {
    showLoading();
    const { error, data } = await contractsAPIs.importContractsSwitch({
      newToken,
      file,
    });
    hideLoading();
    toggleShowImportFeedback();
    setContractsImportFeedbackData({
      ...data,
      validationErrors: error?.response?.data,
    });

    fetchContracts({
      pod_pdr,
      condo_vat_cf,
      owner,
      holder,
      offset,
      limit,
      customer,
      customerType,
      managementStatus,
      contractStatus,
      contractSigningYear,
      contractSigningMonth,
      contractEndYear,
      contractEndMonth,
      contractType,
      supplier,
    });
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
    }
  };

  async function handleFileListView(key) {
      showLoading();
  
      const { data, error } = await getSignedUrl(key, newToken);
  
      if (error) {
        hideLoading();
        alert.error(t('COMMON:GENERIC_ERROR'));
        return;
      }
  
      hideLoading();
      window.open(data.url, '_blank');
    }

  useEffect(() => {
    fetchContracts({
      year,
      pod_pdr,
      condo_vat_cf,
      owner,
      holder,
      offset,
      limit,
      customer,
      customerType,
      managementStatus,
      contractStatus,
      contractSigningYear,
      contractSigningMonth,
      contractEndYear,
      contractEndMonth,
      contractType,
      supplier,
    });
    fetchContractsForExport({
      year,
      pod_pdr,
      condo_vat_cf,
      owner,
      holder,
      offset: 0,
      limit: 99999,
      customer,
      customerType,
      managementStatus,
      contractStatus,
      contractSigningYear,
      contractSigningMonth,
      contractEndYear,
      contractEndMonth,
      contractType,
      supplier,
    });
  }, [
    year,
    condo_vat_cf,
    pod_pdr,
    owner,
    holder,
    offset,
    limit,
    customer,
    customerType,
    managementStatus,
    contractStatus,
    contractSigningYear,
    contractSigningMonth,
    contractEndYear,
    contractEndMonth,
    contractType,
    supplier,
  ]);

  return (
    <ContractsPresentational
      contractsImportFeedbackData={contractsImportFeedbackData}
      data={contracts}
      formattedData={formattedCSVContracts}
      profile={profile}
      query={query}
      total={total}
      totPod={totPod}
      totPdr={totPdr}
      onFilter={onFilter}
      onImportContract={onImportContract}
      toggleShowImportFeedback={toggleShowImportFeedback}
      hasImportedContracts={hasImportedContracts}
      handleFileListView={handleFileListView}
    />
  );
};

export default ContractsSuperAdmin;
